<script>
import Form from '@classes/Form'
import { formatLongDate } from '@utils/format-date'
import DatePicker from '@components/date-picker.vue'
import PasswordComponent from '@components/password/password-component.vue'
import TermsOfServicePage from '@views/terms.vue'
import PrivacyPolicyPage from '@views/_privacy.vue'
import { userService } from '@services/UserService'
import layout from '@src/mixins/layout.vue'

export default {
  components: {
    DatePicker,
    PasswordComponent,
    PrivacyPolicyPage,
    TermsOfServicePage,
  },
  page: {
    title: 'Signup',
    meta: [{ name: 'description', content: 'The Signup page.' }],
  },
  mixins: [layout],
  props: {
    userDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      valid: false,
      dialog: false,
      isShowAlert: false,
      isShowPrivacy: false,
      showType: '',
      form: new Form({
        user_id: this.userDetails?.user_id,
        first_name: this.userDetails?.first_name,
        last_name: this.userDetails?.last_name,
        sex: this.userDetails?.gender,
        email: this.userDetails?.email,
        password: '',
        dob: this.userDetails?.dob,
        signup_code: this.userDetails?.signup_code,
      }),
      selectedSex: this.userDetails?.gender,
      genderOptions: [],
      clearError: false,
      rules: {
        emailRules: [
          (v) => !!v || 'E-mail is required',
          (v) => /^\S+@\S+\.\S+$/.test(v) || 'E-mail must be valid',
        ],
      },
      termsCheck: false,
      showCheckEmailText: false,
    }
  },
  computed: {
    getDialogTitle() {
      if (this.showType === 'terms') {
        return 'Terms Of Service'
      } else if (this.showType === 'privacy') {
        return 'Privacy Policy'
      } else return ''
    },
  },
  async mounted() {
    try {
      const optionsData = (await userService.options()).data
      if (optionsData) {
        this.genderOptions = optionsData.gender
        this.selectedSex = this.userDetails.gender
        this.form.sex = this.selectedSex.key
      }
    } catch (error) {
      console.warn(error.response)
    }
  },
  methods: {
    formatLongDate,
    setPassword(enteredPassword) {
      this.form.password = enteredPassword
    },
    clearFormFromErrors(eventName) {
      this.clearError = true
      this.form.errors.clear(eventName)
      this.$refs.form.validate()
    },
    validate() {
      this.$refs.form.validate()
      if (this.valid) {
        this.tryingToRegister = true
        this.register()
      }
    },
    showAlert() {
      this.dialog = true
      this.isShowAlert = true
    },
    showPrivacyDlg(type) {
      this.isShowPrivacy = true
      this.showType = type
    },
    closePrivacyDlg() {
      this.isShowPrivacy = false
      this.showType = ''
    },
    async register() {
      this.clearError = false
      try {
        this.form.is_terms_privacy_policy_accepted = this.termsCheck
        const registerUser = await userService.register(this.form)
        if (registerUser.status === 201) {
          this.showCheckEmailText = true
          this.tryingToRegister = false
          this.dialog = true
        }
      } catch (error) {
        console.warn(error.response)
        this.form.onFail(error.response.data)
        this.tryingToRegister = false
      }
    },
    navigatetoSignIn() {
      this.$router.push({ name: 'safehaven-login' })
    },
  },
}
</script>

<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col align="center" class="mb-4" :class="$style.title">
        Create an account
      </v-col>
    </v-row>
    <v-row no-gutters justify="center">
      <v-form ref="form" v-model="valid" @submit.prevent="validate">
        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="6"
            xl="6"
            :class="$style.text"
            class="py-0"
          >
            <div class="mb-2" :class="$style.labelText"
              >First Name<span :class="$style.mandatory">*</span></div
            >
            <v-text-field
              v-model="form.first_name"
              disabled
              hide-details="auto"
              dense
              :class="$style.textField"
              outlined
              readonly
            ></v-text-field>
          </v-col>

          <v-col
            cols="12"
            md="6"
            lg="6"
            xl="6"
            :class="$style.text"
            class="py-0"
          >
            <div class="mb-2" :class="$style.labelText">
              Last Name<span :class="$style.mandatory">*</span>
            </div>
            <v-text-field
              v-model="form.last_name"
              hide-details="auto"
              dense
              disabled
              :class="$style.textField"
              outlined
              readonly
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="6"
            xl="6"
            :class="$style.text"
            class="py-0"
          >
            <div class="mb-2" :class="$style.labelText"
              >Date of birth<span :class="$style.mandatory">*</span></div
            >
            <DatePicker
              :date="form.dob ? formatLongDate(form.dob) : null"
              label=""
              dense
              hide-details="auto"
              outlined
              disabled
              readonly
              append-icon="mdi-calendar-month"
              safe-haven
            ></DatePicker>
          </v-col>

          <v-col
            cols="12"
            md="6"
            lg="6"
            xl="6"
            :class="$style.text"
            class="py-0"
          >
            <div class="mb-2" :class="$style.labelText"
              >Gender<span :class="$style.mandatory">*</span></div
            >
            <v-select
              v-model="selectedSex"
              outlined
              :class="$style.textField"
              :items="genderOptions"
              item-text="value"
              item-value="key"
              disabled
              readonly
              dense
              hide-details="auto"
            >
            </v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="6"
            xl="6"
            :class="$style.text"
            class="py-0"
          >
            <div class="mb-2" :class="$style.labelText"
              >Email<span :class="$style.mandatory">*</span></div
            >
            <v-text-field
              v-model="form.email"
              hide-details="auto"
              type="email"
              :rules="rules.emailRules"
              :error-messages="form.errors.errors.email"
              dense
              :class="$style.textField"
              outlined
              required
              append-icon="mdi-email-outline"
              @change="clearFormFromErrors('email')"
            ></v-text-field>
          </v-col>
          <v-col
            cols="0"
            md="6"
            lg="6"
            xl="6"
            :class="$style.text"
            class="py-0 my-0"
          ></v-col>
        </v-row>

        <PasswordComponent
          password-label="Password"
          confirm-label="Confirm Password"
          :error-message="clearError ? [] : form.errors.errors.password"
          @set-password="setPassword"
          @clear-form="clearFormFromErrors('password')"
        ></PasswordComponent>
        <v-row
          no-gutters
          :class="$style.representativeText"
          justify="center"
          class="mt-4"
        >
          <v-col cols="12">
            Passwords must contain at least 8 characters with uppercase and
            lowercase, a number and a special character.
          </v-col>
        </v-row>

        <v-row class="mt-4" :class="$style.signUpText" no-gutters>
          <v-col
            cols="12"
            :align="!notDesktop ? 'center' : 'start'"
            :class="$style.termsCol"
          >
            <v-checkbox
              v-model="termsCheck"
              hide-details
              class="shrink mb-1 mt-0"
            ></v-checkbox>
            <div>
              <span :class="$style.checkText">
                I agree to HealthBook+

                <a
                  :class="$style.privacyText"
                  @click="() => showPrivacyDlg('terms')"
                >
                  Terms of Service
                </a>
                and
                <a
                  :class="$style.privacyText"
                  @click="() => showPrivacyDlg('privacy')"
                >
                  Privacy Policy
                </a>
              </span>
            </div>
          </v-col>
        </v-row>

        <v-row justify="center" class="mb-2" no-gutters>
          <v-col cols="12" md="8" align="center">
            <v-btn
              type="submit"
              class="mt-4 primary-btn w-m-90-btn"
              :disabled="!valid || !termsCheck"
              @click="showAlert"
            >
              Sign Up
            </v-btn>
          </v-col>
          <v-dialog v-if="showCheckEmailText" v-model="dialog" max-width="290">
            <v-card class="justify-center">
              <v-card-title class="text-h5">
                <v-spacer></v-spacer>
                <v-btn icon @click="navigatetoSignIn">
                  <v-icon>mdi-close</v-icon>
                </v-btn></v-card-title
              >

              <v-card-text class="justify-center">
                Please check your email to confirm your account.
              </v-card-text>

              <v-card-actions class="justify-center">
                <v-btn color="#0476D9" text @click="navigatetoSignIn">
                  ok
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </v-form>
    </v-row>
    <v-dialog v-model="isShowPrivacy" scrollable max-width="1300px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="closePrivacyDlg">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ getDialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text style="min-height: 600px">
          <TermsOfServicePage v-if="isShowPrivacy && showType === 'terms'" />
          <PrivacyPolicyPage v-if="isShowPrivacy && showType === 'privacy'" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.labelText {
  font-size: 12px !important;
  font-weight: 700;
}
.mandatory {
  color: #da1414;
}
.title {
  font-size: 1.25em;
}
.text,
.representativeText {
  margin: 0.5rem auto;
  font-size: 12px !important;
  color: #2c3a4b;
}
.textField {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.75px;
}
.signUpText {
  font-size: 12px;
  color: #858c94;
}
.inlineLink {
  display: inline;
  text-decoration: none !important;
}
.termsCol {
  display: flex !important;
  align-items: center;
  justify-content: center;
}
.lightText {
  font-size: 15px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.55) !important;
}
.privacyText {
  font-size: 15px;
  font-weight: 700;
  line-height: 10px;
  color: #0476d9;
  text-align: left;
  text-decoration: none;
  letter-spacing: 0;
}
.checkText {
  font-size: 15px !important;
  font-weight: 400;
  line-height: 10px;
  color: #6d7580;
  text-decoration: none;
}
.checkEmailText {
  margin-top: 5px;
  font-size: 15px;
  font-weight: 400;
  line-height: 23px;
  color: #23a757;
  text-align: center;
}
@media screen and (max-width: 960px) {
  .termsCol {
    align-items: flex-start;
  }
}
</style>

<style scoped>
/* custom css to override vuetify classes */
/* stylelint-disable */
/deep/ .theme--light.v-input--is-disabled input {
  -webkit-text-fill-color: #999 !important ;
  color: #999 !important;
}

/deep/ .theme--light.v-select .v-select__selection--disabled {
  -webkit-text-fill-color: #999 !important;
  color: #999 !important;
}

.v-input--checkbox >>> .v-icon.v-icon {
  color: #0476d9 !important;
}
/* stylelint-enabled */
</style>
